import {
  createContext, useContext, useMemo, useState, useCallback
} from 'react';

import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { ZBError } from '@zenbusiness/application-commons-logging';

import { useProductLandingContext } from '#client/pages/ProductLanding/contexts/useProductLanding';
import { useProductCoupon } from '#client/pages/ProductLanding/hooks/useProductCoupon';
import { useTaxClassification } from '#client/pages/ProductLanding/hooks/useTaxClassification';

// Helper function to convert package name to URL-safe format
const formatPackageNameForUrl = (packageName) => packageName
  .replace(/\+/g, 'and') // Replace + with 'and'
  .replace(/ /g, '-') // Replace spaces with dashes
  .toLowerCase();

const BuyBoxContext = createContext();

export const useBuyBoxContext = () => {
  const context = useContext(BuyBoxContext);

  if (!context) {
    throw new ZBError('useBuyBoxContext must be used within a BuyBoxProvider');
  }

  return context;
};

export const BuyBoxProvider = ({ children }) => {
  const {
    entitlements,
    orderTasksWithStatus,
    packages,
    comparePackages,
    plans,
    getBuyButtonConfig
  } = useProductLandingContext();

  const {
    businessEntity: {
      domesticJurisdiction, formedBy, formationStatus, type
    }
  } = useBusinessEntity();

  const { formatMessage: fm } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();

  // Package selection logic
  const packageName = searchParams.get('package');
  const packageIndex = packages?.findIndex(
    (pkg) => pkg.packageName && formatPackageNameForUrl(fm({ id: pkg.packageName })) === packageName
  ) ?? -1;

  const [packageActive, setPackageActive] = useState(
    packageIndex !== -1 ? packageIndex : 0
  );

  // Active package data (memoized)
  const activePackage = useMemo(() => packages?.[packageActive] || {}, [packages, packageActive]);

  const {
    bulletPoints,
    ctaIcon,
    couponBanner,
    interactService = {},
    note,
    packageNote,
    packageLabel,
    packageColor = '',
    productName,
    showPreviousPurchasedCallout
  } = activePackage;

  const {
    ctaType,
    service,
    openInNewTab = false,
    isBusinessTypeDependent = false,
    isTaxClassificationDependent = false,
    isPurchasedByOrderTask = false,
    interval
  } = interactService;

  // Fetch tax classification when needed
  const { taxClassification } = useTaxClassification({ skip: !isTaxClassificationDependent });

  // Service code logic
  const serviceCode = useMemo(() => {
    if (!service) return '';

    // Dynamic service resolver function
    if (typeof service === 'function') {
      return taxClassification ? service(taxClassification) : '';
    }

    // Business type prefix pattern
    if (isBusinessTypeDependent) {
      return `${type}_${service}`;
    }

    return service;
  }, [service, taxClassification, isBusinessTypeDependent, type]);

  const finalCtaType = useMemo(() => (typeof ctaType === 'function' ? ctaType(serviceCode) : ctaType), [ctaType, serviceCode]);

  // Enhanced entitlements with order task services
  const enhancedEntitlements = useMemo(() => {
    if (!isPurchasedByOrderTask || !orderTasksWithStatus?.length) {
      return entitlements;
    }

    // Check if service exists in orderTasksWithStatus
    const hasServiceInOrderTasks = orderTasksWithStatus.some(
      (orderTask) => orderTask.type === serviceCode || orderTask.data?.service === serviceCode
    );

    if (!hasServiceInOrderTasks) {
      return entitlements;
    }

    // Add service code to entitlements
    return {
      ...entitlements,
      has: (key) => {
        if (key === serviceCode) return true;
        return entitlements?.has(key);
      }
    };
  }, [entitlements, isPurchasedByOrderTask, orderTasksWithStatus, serviceCode]);

  const { enabledCoupon, serviceToAddToCart } = useProductCoupon({
    entitlements: enhancedEntitlements,
    serviceCode,
    interval
  });

  // Pricing and CTA props
  const ctaProps = useMemo(
    () => getBuyButtonConfig?.(
      enhancedEntitlements,
      serviceCode,
      domesticJurisdiction,
      formedBy,
      formationStatus
    ) || {},
    [getBuyButtonConfig, enhancedEntitlements, serviceCode,
      domesticJurisdiction, formedBy, formationStatus]
  );

  // Computed values
  const displayCallout = Boolean(enhancedEntitlements?.has(service)
    && showPreviousPurchasedCallout);

  // Coupon banner logic - show when coupons are available for this product
  const displayCouponBanner = Boolean(couponBanner && serviceToAddToCart?.couponUuid);
  const couponBannerMessage = couponBanner?.message;

  // Event handlers (memoized)
  const handleTabChange = useCallback((_, newValue) => {
    setPackageActive(newValue);
    const pkgName = fm({ id: packages[newValue].packageName });
    setSearchParams({ package: formatPackageNameForUrl(pkgName) });
  }, [packages, fm, setSearchParams]);

  const handleComparePackagesClick = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('m', 'comparePackages');
    if (plans) setSearchParams(newParams);
  }, [searchParams, plans, setSearchParams]);

  const contextValue = useMemo(() => ({
    // Package data
    packages,
    packageActive,
    activePackage,
    comparePackages,
    plans,

    // Active package properties
    bulletPoints,
    ctaIcon,
    ctaType: finalCtaType,
    couponBannerMessage,
    displayCouponBanner,
    note,
    packageNote,
    packageLabel,
    packageColor,
    productName,
    serviceCode,
    openInNewTab,
    interval,
    enabledCoupon,
    serviceToAddToCart,
    // Computed values
    ctaProps,
    displayCallout,

    // Event handlers
    handleTabChange,
    handleComparePackagesClick
  }), [
    packages,
    packageActive,
    activePackage,
    comparePackages,
    plans,
    bulletPoints,
    ctaIcon,
    finalCtaType,
    note,
    packageNote,
    packageLabel,
    packageColor,
    productName,
    serviceCode,
    openInNewTab,
    ctaProps,
    displayCallout,
    displayCouponBanner,
    couponBannerMessage,
    handleTabChange,
    handleComparePackagesClick,
    interval,
    enabledCoupon,
    serviceToAddToCart
  ]);

  return (
    <BuyBoxContext.Provider value={contextValue}>
      {children}
    </BuyBoxContext.Provider>
  );
};

BuyBoxProvider.propTypes = {
  children: PropTypes.node.isRequired
};
